import { useEffect, useState, type FormEvent } from "react";
import { Mail } from "lucide-react";

// Email code sign-in (eng-docs "AD - Email Code Sign-in"). The backend answers the same whether or not the address can
// sign in, so the code step always follows and never says which.

export interface EmailCodeSignInProps {
  /** Signs in with the proof; resolves to an error message, or null on success. */
  onProof: (proof: string) => Promise<string | null>;
  /** A portal client invite: the code goes to the invited address only. */
  inviteToken?: string;
  /** A sandbox join link. */
  magicLinkToken?: string;
  /** Locks the address, e.g. to the one an invite was sent to. */
  email?: string;
  apiBase?: string;
  label?: string;
}

const RESEND_SECONDS = 60;

const buttonBase =
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
const outlineButton = `${buttonBase} h-10 w-full border border-input bg-background px-6 shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50`;
const primaryButton = `${buttonBase} h-10 w-full bg-primary px-6 text-primary-foreground shadow-xs hover:bg-primary/90`;
const linkButton = "text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50";
const inputClass =
  "h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none placeholder:text-muted-foreground " +
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50 dark:bg-input/30";

const TOO_MANY = "Too many attempts. Wait a minute and try again.";

export function EmailCodeSignIn({
  onProof,
  inviteToken,
  magicLinkToken,
  email: fixedEmail,
  apiBase = "/api/proxy/v1",
  label = "Continue with email",
}: EmailCodeSignInProps) {
  const [step, setStep] = useState<"start" | "email" | "code">("start");
  const [email, setEmail] = useState(fixedEmail ?? "");
  // Newest first. A resend the backend dropped never sends a code, so earlier codes must keep working.
  const [challengeIds, setChallengeIds] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const sendCode = async (address: string, resend = false) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await fetch(`${apiBase}/auth/email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: address, inviteToken, magicLinkToken }),
      });
      if (resp.status === 429) return setError(TOO_MANY);
      if (!resp.ok) return setError("Enter a valid email address.");
      const data: { challengeId: string } = await resp.json();
      setChallengeIds((ids) => (resend ? [data.challengeId, ...ids] : [data.challengeId]));
      setCode("");
      setStep("code");
      setResendIn(RESEND_SECONDS);
      if (resend) setNotice("If this email can sign in, another code is on its way. A code from any of the emails works.");
    } catch {
      setError("Couldn't send the code. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyOne = (challengeId: string) =>
    fetch(`${apiBase}/auth/email-code/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challengeId, code }),
    });

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    if (challengeIds.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      let resp: Response | null = null;
      for (const id of challengeIds) {
        resp = await verifyOne(id);
        if (resp.ok || resp.status === 429) break;
      }
      if (resp?.status === 429) return setError(TOO_MANY);
      if (!resp?.ok) return setError("That code didn't work. Check it or send a new one.");
      const data: { proof: string } = await resp.json();
      const signInError = await onProof(data.proof);
      if (signInError) setError(signInError);
    } catch {
      setError("Couldn't check the code. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    if (fixedEmail) void sendCode(fixedEmail);
    else setStep("email");
  };

  const message = (error || notice) && (
    <p className={`text-center text-sm ${error ? "text-destructive" : "text-muted-foreground"}`} role={error ? "alert" : "status"}>
      {error ?? notice}
    </p>
  );

  if (step === "start") {
    return (
      <div className="flex w-full flex-col gap-2">
        <button type="button" className={outlineButton} onClick={start} disabled={busy} data-testid="email-code-start">
          <Mail className="size-4" />
          {busy ? "Sending code..." : label}
        </button>
        {message}
      </div>
    );
  }

  if (step === "email") {
    return (
      <form
        className="flex w-full flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void sendCode(email);
        }}
      >
        <label htmlFor="email-code-address" className="text-left text-sm font-medium">
          Work email
        </label>
        <input
          id="email-code-address"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className={inputClass}
          disabled={busy}
        />
        <button type="submit" className={primaryButton} disabled={busy || !email.trim()}>
          {busy ? "Sending code..." : "Send code"}
        </button>
        <button type="button" className={linkButton} onClick={() => setStep("start")} disabled={busy}>
          Back
        </button>
        {message}
      </form>
    );
  }

  return (
    <form className="flex w-full flex-col gap-2" onSubmit={verify}>
      <p className="text-center text-sm text-muted-foreground">
        If this email can sign in, we've sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>.
        It expires in 10 minutes.
      </p>
      <label htmlFor="email-code-value" className="sr-only">
        Sign-in code
      </label>
      <input
        id="email-code-value"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        autoFocus
        required
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        className={`${inputClass} text-center font-mono text-lg tracking-[0.5em]`}
        disabled={busy}
        data-testid="email-code-input"
      />
      <button type="submit" className={primaryButton} disabled={busy || code.length !== 6}>
        {busy ? "Signing in..." : "Sign in"}
      </button>
      <div className="flex items-center justify-between">
        <button type="button" className={linkButton} onClick={() => sendCode(email, true)} disabled={busy || resendIn > 0}>
          {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
        </button>
        {!fixedEmail && (
          <button
            type="button"
            className={linkButton}
            onClick={() => {
              setStep("email");
              setError(null);
              setNotice(null);
            }}
            disabled={busy}
          >
            Use a different email
          </button>
        )}
      </div>
      {message}
    </form>
  );
}
